import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { 
  Heart, TrendingDown, TrendingUp, AlertTriangle, Users, Activity,
  Phone, MessageSquare, Calendar, RefreshCw, ChevronRight, Star,
  Brain, Smile, Frown, Meh, Target, Clock, CheckCircle2, XCircle
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const CaregiverConfidenceAnalytics = () => {
  const [dashboard, setDashboard] = useState(null);
  const [caregivers, setCaregivers] = useState([]);
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('7d');
  const { toast } = useToast();

  const token = localStorage.getItem('token');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase45/caregiver-analytics/dashboard?range=${dateRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
        setCaregivers(data.caregivers || []);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Trigger outreach
  const triggerOutreach = async (caregiverId, outreachType) => {
    try {
      const res = await fetch(`${API}/api/phase45/caregiver-analytics/outreach`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          caregiver_id: caregiverId,
          outreach_type: outreachType
        })
      });

      if (res.ok) {
        toast({
          title: "Outreach Initiated",
          description: `${outreachType === 'social_worker' ? 'Social Worker' : 'Nurse'} outreach has been scheduled.`
        });
        fetchDashboard();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to initiate outreach", variant: "destructive" });
    }
  };

  // Get confidence score color
  const getScoreColor = (score) => {
    if (score >= 4) return 'text-green-600';
    if (score >= 3) return 'text-yellow-600';
    if (score >= 2) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 4) return 'bg-green-100';
    if (score >= 3) return 'bg-yellow-100';
    if (score >= 2) return 'bg-orange-100';
    return 'bg-red-100';
  };

  const getScoreEmoji = (score) => {
    if (score >= 4) return <Smile className="h-5 w-5 text-green-500" />;
    if (score >= 3) return <Meh className="h-5 w-5 text-yellow-500" />;
    return <Frown className="h-5 w-5 text-red-500" />;
  };

  const getBurnoutRiskLevel = (risk) => {
    if (risk >= 70) return { label: 'High Risk', color: 'bg-red-500', textColor: 'text-red-700' };
    if (risk >= 40) return { label: 'Moderate Risk', color: 'bg-orange-500', textColor: 'text-orange-700' };
    return { label: 'Low Risk', color: 'bg-green-500', textColor: 'text-green-700' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Heart className="h-7 w-7 text-rose-500" />
            Caregiver Confidence Analytics
          </h1>
          <p className="text-gray-600">Population health insights for caregiver burnout prevention</p>
        </div>
        <div className="flex gap-2">
          <select 
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="border rounded-md px-3 py-2"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <Button variant="outline" onClick={fetchDashboard}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rose-600 font-medium">High Burnout Risk</p>
                <p className="text-3xl font-bold text-rose-700">
                  {dashboard?.summary?.high_risk_count || 0}
                </p>
              </div>
              <AlertTriangle className="h-10 w-10 text-rose-500" />
            </div>
            <p className="text-xs text-rose-600 mt-2">Needs immediate outreach</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Declining Trend</p>
                <p className="text-3xl font-bold text-orange-700">
                  {dashboard?.summary?.declining_count || 0}
                </p>
              </div>
              <TrendingDown className="h-10 w-10 text-orange-500" />
            </div>
            <p className="text-xs text-orange-600 mt-2">Confidence dropping</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Avg Confidence</p>
                <p className="text-3xl font-bold text-green-700">
                  {dashboard?.summary?.avg_confidence?.toFixed(1) || '0.0'}
                </p>
              </div>
              <Star className="h-10 w-10 text-green-500" />
            </div>
            <p className="text-xs text-green-600 mt-2">Out of 5.0</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Caregivers</p>
                <p className="text-3xl font-bold text-blue-700">
                  {dashboard?.summary?.total_caregivers || 0}
                </p>
              </div>
              <Users className="h-10 w-10 text-blue-500" />
            </div>
            <p className="text-xs text-blue-600 mt-2">Active in period</p>
          </CardContent>
        </Card>
      </div>

      {/* Auto-Outreach Alert */}
      {(dashboard?.summary?.high_risk_count || 0) > 0 && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Brain className="h-6 w-6 text-rose-600" />
                <div>
                  <h3 className="font-semibold text-rose-900">
                    {dashboard?.summary?.high_risk_count} Caregivers Need Support
                  </h3>
                  <p className="text-sm text-rose-700">
                    AI detected high burnout risk based on declining confidence scores
                  </p>
                </div>
              </div>
              <Button className="bg-rose-600 hover:bg-rose-700">
                <Phone className="h-4 w-4 mr-2" />
                Trigger Bulk Outreach
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="overview">Population View</TabsTrigger>
          <TabsTrigger value="individuals">Individual Analysis</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>

        {/* Population Overview */}
        <TabsContent value="overview" className="space-y-4">
          {/* Risk Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Burnout Risk Distribution</CardTitle>
              <CardDescription>Caregivers segmented by risk level</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* High Risk */}
                <div className="flex items-center gap-4">
                  <div className="w-24 text-sm font-medium text-red-600">High Risk</div>
                  <div className="flex-1">
                    <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-red-500 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${(dashboard?.distribution?.high || 0) / (dashboard?.summary?.total_caregivers || 1) * 100}%` }}
                      >
                        <span className="text-white text-xs font-bold">{dashboard?.distribution?.high || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Moderate Risk */}
                <div className="flex items-center gap-4">
                  <div className="w-24 text-sm font-medium text-orange-600">Moderate</div>
                  <div className="flex-1">
                    <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-orange-500 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${(dashboard?.distribution?.moderate || 0) / (dashboard?.summary?.total_caregivers || 1) * 100}%` }}
                      >
                        <span className="text-white text-xs font-bold">{dashboard?.distribution?.moderate || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Low Risk */}
                <div className="flex items-center gap-4">
                  <div className="w-24 text-sm font-medium text-green-600">Low Risk</div>
                  <div className="flex-1">
                    <div className="h-8 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full flex items-center justify-end pr-2"
                        style={{ width: `${(dashboard?.distribution?.low || 0) / (dashboard?.summary?.total_caregivers || 1) * 100}%` }}
                      >
                        <span className="text-white text-xs font-bold">{dashboard?.distribution?.low || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* High Risk Caregivers List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Priority Outreach List
              </CardTitle>
              <CardDescription>Caregivers requiring immediate support</CardDescription>
            </CardHeader>
            <CardContent>
              {caregivers.filter(c => c.burnout_risk >= 70).length > 0 ? (
                <div className="space-y-3">
                  {caregivers.filter(c => c.burnout_risk >= 70).map((caregiver, i) => (
                    <div key={i} className="flex items-center justify-between p-4 border rounded-lg bg-red-50 border-red-200">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-full ${getScoreBg(caregiver.avg_confidence)}`}>
                          {getScoreEmoji(caregiver.avg_confidence)}
                        </div>
                        <div>
                          <h4 className="font-medium">{caregiver.name}</h4>
                          <p className="text-sm text-gray-600">
                            Caring for: {caregiver.patient_name}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className="bg-red-500">
                              {caregiver.burnout_risk}% Risk
                            </Badge>
                            <span className="text-xs text-gray-500">
                              Avg Score: {caregiver.avg_confidence?.toFixed(1)}
                            </span>
                            {caregiver.trend === 'declining' && (
                              <TrendingDown className="h-4 w-4 text-red-500" />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => triggerOutreach(caregiver.id, 'social_worker')}
                        >
                          <MessageSquare className="h-4 w-4 mr-1" />
                          SW Call
                        </Button>
                        <Button 
                          size="sm"
                          className="bg-red-500 hover:bg-red-600"
                          onClick={() => triggerOutreach(caregiver.id, 'nurse')}
                        >
                          <Phone className="h-4 w-4 mr-1" />
                          Nurse Call
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-2" />
                  <p className="text-green-700 font-medium">No High-Risk Caregivers</p>
                  <p className="text-sm text-gray-500">All caregivers are within acceptable ranges</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Individual Analysis */}
        <TabsContent value="individuals" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">All Caregivers</CardTitle>
              <CardDescription>Click to view detailed analysis</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {caregivers.map((caregiver, i) => {
                  const riskLevel = getBurnoutRiskLevel(caregiver.burnout_risk);
                  return (
                    <div 
                      key={i}
                      className="flex items-center justify-between p-3 border rounded-lg hover:border-teal-300 cursor-pointer transition-colors"
                      onClick={() => setSelectedCaregiver(caregiver)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${getScoreBg(caregiver.avg_confidence)}`}>
                          {getScoreEmoji(caregiver.avg_confidence)}
                        </div>
                        <div>
                          <h4 className="font-medium">{caregiver.name}</h4>
                          <p className="text-xs text-gray-500">{caregiver.patient_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className={`font-bold ${getScoreColor(caregiver.avg_confidence)}`}>
                            {caregiver.avg_confidence?.toFixed(1)}
                          </p>
                          <p className="text-xs text-gray-500">avg score</p>
                        </div>
                        <Badge className={riskLevel.color}>
                          {riskLevel.label}
                        </Badge>
                        {caregiver.trend === 'declining' && (
                          <TrendingDown className="h-4 w-4 text-red-500" />
                        )}
                        {caregiver.trend === 'improving' && (
                          <TrendingUp className="h-4 w-4 text-green-500" />
                        )}
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Selected Caregiver Detail */}
          {selectedCaregiver && (
            <Card className="border-teal-200">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{selectedCaregiver.name}</CardTitle>
                    <CardDescription>Caring for {selectedCaregiver.patient_name}</CardDescription>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedCaregiver(null)}>
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className={`text-2xl font-bold ${getScoreColor(selectedCaregiver.avg_confidence)}`}>
                      {selectedCaregiver.avg_confidence?.toFixed(1)}
                    </p>
                    <p className="text-xs text-gray-500">Avg Confidence</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-800">
                      {selectedCaregiver.check_ins || 0}
                    </p>
                    <p className="text-xs text-gray-500">Check-ins</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-800">
                      {selectedCaregiver.days_since_outreach || 'N/A'}
                    </p>
                    <p className="text-xs text-gray-500">Days Since Outreach</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className={`text-2xl font-bold ${
                      selectedCaregiver.burnout_risk >= 70 ? 'text-red-600' :
                      selectedCaregiver.burnout_risk >= 40 ? 'text-orange-600' : 'text-green-600'
                    }`}>
                      {selectedCaregiver.burnout_risk}%
                    </p>
                    <p className="text-xs text-gray-500">Burnout Risk</p>
                  </div>
                </div>

                {/* Score History */}
                <div className="mb-4">
                  <h4 className="font-medium mb-2">Recent Scores</h4>
                  <div className="flex gap-2">
                    {(selectedCaregiver.recent_scores || []).map((score, i) => (
                      <div 
                        key={i}
                        className={`w-10 h-10 rounded-full ${getScoreBg(score)} flex items-center justify-center`}
                      >
                        <span className={`font-bold ${getScoreColor(score)}`}>{score}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Risk Factors */}
                {selectedCaregiver.risk_factors?.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium mb-2">Risk Factors</h4>
                    <div className="space-y-2">
                      {selectedCaregiver.risk_factors.map((factor, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-red-50 rounded">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <span className="text-sm text-red-700">{factor}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => triggerOutreach(selectedCaregiver.id, 'social_worker')}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Schedule SW Call
                  </Button>
                  <Button 
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                    onClick={() => triggerOutreach(selectedCaregiver.id, 'nurse')}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Schedule Nurse Call
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Trends */}
        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Confidence Score Trends</CardTitle>
              <CardDescription>Population-level trends over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Trend Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <TrendingUp className="h-8 w-8 mx-auto text-green-500 mb-2" />
                    <p className="text-2xl font-bold text-green-700">
                      {dashboard?.trends?.improving || 0}
                    </p>
                    <p className="text-sm text-green-600">Improving</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Activity className="h-8 w-8 mx-auto text-gray-500 mb-2" />
                    <p className="text-2xl font-bold text-gray-700">
                      {dashboard?.trends?.stable || 0}
                    </p>
                    <p className="text-sm text-gray-600">Stable</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <TrendingDown className="h-8 w-8 mx-auto text-red-500 mb-2" />
                    <p className="text-2xl font-bold text-red-700">
                      {dashboard?.trends?.declining || 0}
                    </p>
                    <p className="text-sm text-red-600">Declining</p>
                  </div>
                </div>

                {/* Weekly Average */}
                <div>
                  <h4 className="font-medium mb-3">Weekly Average Score</h4>
                  <div className="flex items-end gap-2 h-32">
                    {(dashboard?.weekly_averages || [1,2,3,4,5,6,7].map(() => Math.random() * 2 + 3)).map((score, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div 
                          className={`w-full rounded-t ${
                            score >= 4 ? 'bg-green-500' :
                            score >= 3 ? 'bg-yellow-500' :
                            score >= 2 ? 'bg-orange-500' : 'bg-red-500'
                          }`}
                          style={{ height: `${(score / 5) * 100}%` }}
                        />
                        <span className="text-xs text-gray-500 mt-1">W{i + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Outreach Effectiveness */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Outreach Effectiveness</CardTitle>
              <CardDescription>Impact of support interventions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-teal-50 rounded-lg">
                  <h4 className="font-medium text-teal-900 mb-2">Post-Outreach Improvement</h4>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-6 w-6 text-teal-600" />
                    <span className="text-2xl font-bold text-teal-700">
                      {dashboard?.outreach_stats?.improvement_rate || 78}%
                    </span>
                  </div>
                  <p className="text-xs text-teal-600 mt-1">
                    Caregivers show improved confidence after intervention
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Avg Days to Recovery</h4>
                  <div className="flex items-center gap-2">
                    <Clock className="h-6 w-6 text-blue-600" />
                    <span className="text-2xl font-bold text-blue-700">
                      {dashboard?.outreach_stats?.avg_recovery_days || 5}
                    </span>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    Days until confidence returns to baseline
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CaregiverConfidenceAnalytics;
